<template>
  <div class="page-container">
    <div class="header-section">
      <h5>Data Extractor</h5>
      <p class="subtitle">Extract and export data from different sources</p>
    </div>

    <!-- Data Groups Tabs -->
    <div class="data-groups-container">
      <div class="tab-buttons">
        <button 
          :class="['tab-button', activeGroup === 'marketing' ? 'active' : '']" 
          @click="activeGroup = 'marketing'"
          :disabled="false">
          <div class="tab-icon">
            <i class="fas fa-chart-line"></i>
          </div>
          <span>Marketing</span>
          <div :class="['status-badge', activeGroup === 'marketing' ? 'active' : '']">
            {{ activeGroup === 'marketing' ? 'Active' : 'Available' }}
          </div>
        </button>
        <button 
          :class="['tab-button', activeGroup === 'combined' ? 'active' : '']" 
          @click="activeGroup = 'combined'"
          :disabled="false">
          <div class="tab-icon">
            <i class="fas fa-chart-bar"></i>
          </div>
          <span>Combined</span>
          <div :class="['status-badge', activeGroup === 'combined' ? 'active' : '']">
            {{ activeGroup === 'combined' ? 'Active' : 'Available' }}
          </div>
        </button>
        <button 
          :class="['tab-button', activeGroup === 'custom' ? 'active' : '']" 
          @click="activeGroup = 'custom'"
          :disabled="false">
          <div class="tab-icon">
            <i class="fas fa-database"></i>
          </div>
          <span>Custom</span>
          <div :class="['status-badge', activeGroup === 'custom' ? 'active' : '']">
            {{ activeGroup === 'custom' ? 'Active' : 'Available' }}
          </div>
        </button>
      </div>
    </div>

    <!-- Custom SQL Query Component -->
    <CustomSqlQuery v-if="activeGroup === 'custom'" />

    <!-- Combined Data Section -->
    <template v-if="activeGroup === 'combined'">
      <!-- Date Range and Plant ID Selection -->
      <div class="combined-controls-container">
        <div class="date-range-container">
          <div class="date-inputs">
            <div class="date-input-group">
              <label for="combinedDateFrom">From Date:</label>
              <input 
                type="date" 
                id="combinedDateFrom" 
                v-model="combinedDateFrom" 
                class="date-input"
                :max="combinedDateTo">
            </div>
            <div class="date-input-group">
              <label for="combinedDateTo">To Date:</label>
              <input 
                type="date" 
                id="combinedDateTo" 
                v-model="combinedDateTo" 
                class="date-input"
                :min="combinedDateFrom">
            </div>
            <div class="date-input-group">
              <label for="combinedPlantId">Plant ID:</label>
              <input 
                type="number" 
                id="combinedPlantId" 
                v-model.number="combinedPlantId" 
                class="date-input"
                placeholder="Enter Plant ID">
            </div>
          </div>
          <button 
            @click="fetchCombinedData" 
            class="fetch-button"
            :disabled="isLoadingCombined || !combinedDateFrom || !combinedDateTo || !combinedPlantId">
            <i class="fas fa-download" v-if="!isLoadingCombined"></i>
            <i class="fas fa-spinner fa-spin" v-else></i>
            {{ isLoadingCombined ? 'Fetching...' : 'Fetch Data' }}
          </button>
        </div>

        <!-- View Toggle -->
        <div class="view-toggle-container">
          <button 
            :class="['view-toggle-button', combinedViewMode === 'chart' ? 'active' : '']"
            @click="combinedViewMode = 'chart'">
            <i class="fas fa-chart-line"></i> Chart View
          </button>
          <button 
            :class="['view-toggle-button', combinedViewMode === 'table' ? 'active' : '']"
            @click="combinedViewMode = 'table'">
            <i class="fas fa-table"></i> Table View
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoadingCombined" class="loading-container">
        <div class="loading-spinner"></div>
        <p>Fetching data...</p>
      </div>

      <!-- Chart View -->
      <div v-if="!isLoadingCombined && combinedViewMode === 'chart' && hasCombinedData" class="combined-chart-section">
        <div v-if="availableChartDates.length > 0" class="chart-day-picker">
          <div class="chart-day-picker-header">
            <h5>Days to plot</h5>
            <div class="chart-day-mode">
              <button
                type="button"
                :class="['chart-day-mode-button', chartDayMode === 'single' ? 'active' : '']"
                @click="setChartDayMode('single')">
                One day
              </button>
              <button
                type="button"
                :class="['chart-day-mode-button', chartDayMode === 'multiple' ? 'active' : '']"
                :disabled="availableChartDates.length < 2"
                @click="setChartDayMode('multiple')">
                Multiple days
              </button>
            </div>
          </div>
          <div class="chart-day-chips">
            <button
              v-for="date in availableChartDates"
              :key="date"
              type="button"
              :class="['chart-day-chip', selectedChartDates.includes(date) ? 'active' : '']"
              @click="toggleChartDate(date)">
              {{ date }}
            </button>
          </div>
          <div v-if="chartDayMode === 'multiple'" class="chart-day-actions">
            <button type="button" class="select-all-button" @click="selectAllChartDates">Select all</button>
          </div>
          <p v-if="selectedChartDates.length === 0" class="chart-day-hint">Select at least one day to plot.</p>
          <p v-else class="chart-day-hint">Extract Data exports the full fetched range, including days that are not plotted.</p>
        </div>

        <!-- Power Chart -->
        <div class="chart-container">
          <div class="chart-header">
            <h5>Power Chart</h5>
            <div class="chart-controls">
              <button class="custom-button" @click="extractCombinedData('power')">
                <i class="fas fa-download"></i> Extract Data
              </button>
            </div>
          </div>
          <CustomChart
            v-if="combinedPowerDatasets.length > 0"
            chartTitle=""
            chartType="line"
            dataUnit="kW"
            :datasets="combinedPowerDatasets"
            :hiddenLegends="combinedHiddenLegends.power"
            @legends-updated="updateCombinedHiddenLegends('power', $event)"
          />
          <p v-else class="display-text-container">No power data available</p>
        </div>

        <!-- Price Chart -->
        <div class="chart-container">
          <div class="chart-header">
            <h5>Price Chart</h5>
            <div class="chart-controls">
              <button class="custom-button" @click="extractCombinedData('price')">
                <i class="fas fa-download"></i> Extract Data
              </button>
            </div>
          </div>
          <CustomChart
            v-if="combinedPriceDatasets.length > 0"
            chartTitle=""
            chartType="line"
            dataUnit="AUD"
            :datasets="combinedPriceDatasets"
            :hiddenLegends="combinedHiddenLegends.price"
            @legends-updated="updateCombinedHiddenLegends('price', $event)"
          />
          <p v-else class="display-text-container">No price data available</p>
        </div>
      </div>

      <!-- Table View -->
      <div v-if="!isLoadingCombined && combinedViewMode === 'table' && hasCombinedData" class="combined-table-section">
        <div class="table-controls-container">
          <div class="column-selector-container">
            <h4>Select Columns to Export</h4>
            <div class="column-checkboxes">
              <div 
                v-for="column in availableColumns" 
                :key="column.key"
                class="column-checkbox-item">
                <input 
                  type="checkbox" 
                  :id="'col-' + column.key"
                  v-model="selectedColumns"
                  :value="column.key"
                  class="column-checkbox">
                <label :for="'col-' + column.key" class="column-label">{{ column.label }}</label>
              </div>
            </div>
            <div class="column-selector-actions">
              <button @click="selectAllColumns" class="select-all-button">Select All</button>
              <button @click="deselectAllColumns" class="deselect-all-button">Deselect All</button>
            </div>
          </div>
          <div class="export-container">
            <button @click="exportCombinedDataToCSV" class="export-button" :disabled="selectedColumns.length === 0">
              <i class="fas fa-file-csv"></i>
              Export Selected Columns to CSV
            </button>
          </div>
        </div>

        <!-- Table Options -->
        <div class="table-options-container">
          <h4>Table Display Options</h4>
          <div class="table-options-grid">
            <div class="table-option-item">
              <input 
                type="checkbox" 
                id="combineDateTime"
                v-model="tableOptions.combineDateTime"
                class="table-option-checkbox">
              <label for="combineDateTime" class="table-option-label">
                Combine Date and Time Columns
              </label>
            </div>
            <div class="table-option-item">
              <input 
                type="checkbox" 
                id="includePlantId"
                v-model="tableOptions.includePlantId"
                class="table-option-checkbox">
              <label for="includePlantId" class="table-option-label">
                Include Plant ID Column
              </label>
            </div>
            <div class="table-option-item">
              <label for="dateTimeFormat" class="table-option-label">Date/Time Format:</label>
              <select 
                id="dateTimeFormat"
                v-model="tableOptions.dateTimeFormat"
                class="table-option-select">
                <option value="YYYY-MM-DD HH:mm">YYYY-MM-DD HH:mm</option>
                <option value="DD/MM/YYYY HH:mm">DD/MM/YYYY HH:mm</option>
                <option value="MM/DD/YYYY HH:mm">MM/DD/YYYY HH:mm</option>
                <option value="YYYY-MM-DD HH:mm:ss">YYYY-MM-DD HH:mm:ss</option>
                <option value="DD/MM/YYYY HH:mm:ss">DD/MM/YYYY HH:mm:ss</option>
                <option value="MM/DD/YYYY HH:mm:ss">MM/DD/YYYY HH:mm:ss</option>
              </select>
            </div>
          </div>
        </div>

        <div class="data-section">
          <div class="table-header">
            <h3>Data Results ({{ combinedTableData.length }} records)</h3>
          </div>
          
          <div class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th v-for="column in visibleTableColumns" :key="column.key">{{ column.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in combinedTableData" :key="index">
                  <td v-for="column in visibleTableColumns" :key="column.key">
                    {{ formatTableValueWithOptions(row, column) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!isLoadingCombined && !hasCombinedData && combinedHasSearched" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-search"></i>
        </div>
        <h3>No data found</h3>
        <p>Try adjusting your date range or check your connection.</p>
      </div>

      <!-- Error State -->
      <div v-if="combinedError" class="error-container">
        <div class="error-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h3>Error occurred</h3>
        <p>{{ combinedError }}</p>
        <button @click="fetchCombinedData" class="retry-button">
          <i class="fas fa-redo"></i>
          Try Again
        </button>
      </div>
    </template>

    <!-- Marketing Data Section (existing functionality) -->
    <template v-if="activeGroup === 'marketing'">
      <!-- Date Range Selector -->
      <div class="date-range-container">
        <div class="date-inputs">
          <div class="date-input-group">
            <label for="dateFrom">From Date:</label>
            <input 
              type="date" 
              id="dateFrom" 
              v-model="dateFrom" 
              class="date-input"
              :max="dateTo">
          </div>
          <div class="date-input-group">
            <label for="dateTo">To Date:</label>
            <input 
              type="date" 
              id="dateTo" 
              v-model="dateTo" 
              class="date-input"
              :min="dateFrom">
          </div>
        </div>
        <button 
          @click="fetchData" 
          class="fetch-button"
          :disabled="isLoading || !dateFrom || !dateTo">
          <i class="fas fa-download" v-if="!isLoading"></i>
          <i class="fas fa-spinner fa-spin" v-else></i>
          {{ isLoading ? 'Fetching...' : 'Fetch Data' }}
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>Fetching data...</p>
      </div>

      <!-- Data Table -->
      <div v-if="!isLoading && dataList.length > 0" class="data-section">
        <!-- Column Visibility Controls -->
        <div class="column-visibility-container">
          <div class="column-visibility-header">
            <h4>Column Visibility</h4>
            <div class="column-visibility-actions">
              <button @click="selectAllMarketingColumns" class="select-all-button">Select All</button>
              <button @click="deselectAllMarketingColumns" class="deselect-all-button">Deselect All</button>
            </div>
          </div>
          <div class="column-checkboxes">
            <div 
              v-for="column in columnConfig" 
              :key="column.header"
              class="column-checkbox-item">
              <input 
                type="checkbox" 
                :id="'col-' + column.header"
                v-model="column.visible"
                @change="saveColumnVisibilityToCookies"
                class="column-checkbox">
              <label :for="'col-' + column.header" class="column-label">{{ column.header }}</label>
            </div>
          </div>
        </div>

        <div class="table-header">
          <h3>Data Results ({{ dataList.length }} records)</h3>
          <button @click="exportToCSV" class="export-button" :disabled="visibleMarketingColumns.length === 0">
            <i class="fas fa-file-csv"></i>
            Export to CSV
          </button>
        </div>
        
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th v-for="column in visibleMarketingColumns" :key="column.header" :class="getColumnClass(column.header)">{{ column.header }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in dataList" :key="index">
                <td v-for="column in visibleMarketingColumns" :key="column.header" :class="getColumnClass(column.header)">
                  {{ getCellValue(item, column) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!isLoading && dataList.length === 0 && hasSearched" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-search"></i>
        </div>
        <h3>No data found</h3>
        <p>Try adjusting your date range or check your connection.</p>
      </div>

      <!-- Error State -->
      <div v-if="error" class="error-container">
        <div class="error-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h3>Error occurred</h3>
        <p>{{ error }}</p>
        <button @click="fetchData" class="retry-button">
          <i class="fas fa-redo"></i>
          Try Again
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import base64 from 'base-64';
import { apiRequest } from '@/services/apiService';
import { getUserInfo, getDevModeState, setCookie, getCookie } from '@/services/cookieService';
import CustomSqlQuery from '@/components/cards/CustomSqlQuery.vue';
import CustomChart from '@/components/cards/CustomChart.vue';

export default {
  name: 'DataExtractor',
  components: {
    CustomSqlQuery,
    CustomChart
  },
  data() {
    return {
      activeGroup: 'marketing',
      dateFrom: '2025-07-05',
      dateTo: '2025-07-09',
      isLoading: false,
      dataList: [],
      error: null,
      hasSearched: false,
      subDataUrl: '/api/v1.1/data',
      tableHeaders: [
        'Account Name',
        'Active Name', 
        'Email',
        'Phone Number',
        'Plant Name',
        'Create Time',
        'Datalog SN',
        'Device SN',
        'Device Type',
        'ID',
        'Plant Address',
        'Plant ID',
        'User ID',
        'Installer Company',
        'Brand'
      ],
      columnConfig: [
        { header: 'Account Name', field: 'AccountName', visible: true, formatter: null },
        { header: 'Active Name', field: 'ActiveName', visible: true, formatter: null },
        { header: 'Email', field: 'Email', visible: true, formatter: null },
        { header: 'Phone Number', field: 'PhoneNum', visible: true, formatter: null },
        { header: 'Plant Name', field: 'PlantName', visible: true, formatter: null },
        { header: 'Create Time', field: 'createTime', visible: true, formatter: 'formatDate' },
        { header: 'Datalog SN', field: 'datalogSn', visible: true, formatter: null },
        { header: 'Device SN', field: 'deviceSn', visible: true, formatter: null },
        { header: 'Device Type', field: 'deviceType', visible: true, formatter: null },
        { header: 'ID', field: 'id', visible: true, formatter: null },
        { header: 'Plant Address', field: 'plantAddress', visible: true, formatter: null },
        { header: 'Plant ID', field: 'plantId', visible: true, formatter: null },
        { header: 'User ID', field: 'userId', visible: true, formatter: null },
        { header: 'Installer Company', field: 'installerCompany', visible: true, formatter: null },
        { header: 'Brand', field: null, visible: true, formatter: 'getBrand' }
      ],
      // Combined tab data
      combinedDateFrom: '',
      combinedDateTo: '',
      combinedPlantId: null,
      isLoadingCombined: false,
      combinedHasSearched: false,
      combinedError: null,
      combinedViewMode: 'chart', // 'chart' or 'table'
      combinedPowerDatasets: [],
      combinedPriceDatasets: [],
      combinedTableData: [],
      combinedRawDays: [],
      selectedChartDates: [],
      chartDayMode: 'single',
      combinedHiddenLegends: {
        power: [],
        price: []
      },
      chartLabels: this.prepareLabels(5, 288),
      opacityLevelPrediction: 0.7,
      opacityLevelActual: 0.7,
      selectedColumns: [],
      availableColumns: [],
      tableOptions: {
        combineDateTime: false,
        dateTimeFormat: 'YYYY-MM-DD HH:mm',
        includePlantId: false
      }
    };
  },
  computed: {
    hasCombinedData() {
      return this.combinedTableData.length > 0 || 
             this.combinedPowerDatasets.length > 0 || 
             this.combinedPriceDatasets.length > 0;
    },
    availableChartDates() {
      return this.combinedRawDays.map(day => day.date);
    },
    visibleMarketingColumns() {
      return this.columnConfig.filter(col => col.visible);
    },
    visibleTableColumns() {
      let columns = [];
      
      // If no columns selected, show all for viewing; otherwise show only selected
      if (this.selectedColumns.length === 0) {
        columns = [...this.availableColumns];
      } else {
        columns = this.availableColumns.filter(col => 
          this.selectedColumns.includes(col.key)
        );
      }

      // Handle date/time combination
      if (this.tableOptions.combineDateTime) {
        // Check if date or time columns are in the current columns
        const hasDate = columns.some(col => col.key === 'date');
        const hasTime = columns.some(col => col.key === 'time');
        
        if (hasDate || hasTime) {
          // Remove separate date and time columns
          columns = columns.filter(col => 
            col.key !== 'date' && col.key !== 'time'
          );
          // Add combined datetime column at the beginning
          columns.unshift({
            key: 'datetime',
            label: 'Date/Time'
          });
        }
      } else {
        // Remove datetime column if it exists (when combining is disabled)
        columns = columns.filter(col => col.key !== 'datetime');
      }

      // Handle plant ID column
      if (this.tableOptions.includePlantId) {
        // Check if plant ID column already exists
        const hasPlantId = columns.some(col => col.key === 'plantId');
        if (!hasPlantId) {
          columns.unshift({
            key: 'plantId',
            label: 'Plant ID'
          });
        }
      } else {
        // Remove plant ID column if it exists
        columns = columns.filter(col => col.key !== 'plantId');
      }

      return columns;
    }
  },
  methods: {
    handleEnvironmentChange(isDevMode) {
      console.log("isDevMode:", isDevMode);
      this.subDataUrl = isDevMode ? "/dev-api/api/v1.1/data" : "/api/v1.1/data";
    },
    
    readFromCookies() {
      let cookieData = getUserInfo();
      if (cookieData) {
        // Handle any user-specific data if needed
      }

      // Initialize subDataUrl based on dev mode state from cookies
      const devModeState = getDevModeState();
      if (devModeState.isDevMode !== undefined) {
        this.subDataUrl = devModeState.isDevMode ? "/dev-api/api/v1.1/data" : "/api/v1.1/data";
      }
    },

    // Safe array access helper functions
    safeArrayAccess(obj, prop, index) {
      if (!obj || !prop) return '';
      const array = obj[prop];
      if (!Array.isArray(array) || index < 0 || index >= array.length) return '';
      return array[index] !== null && array[index] !== undefined ? array[index] : '';
    },

    safeGet(obj, path) {
      if (!obj || !path) return null;
      const keys = path.split('.');
      let current = obj;
      for (const key of keys) {
        if (current === null || current === undefined) return null;
        current = current[key];
      }
      return current;
    },

    ensureArray(value) {
      if (Array.isArray(value)) return value;
      if (value === null || value === undefined) return [];
      return [];
    },

    async fetchData() {
      if (!this.dateFrom || !this.dateTo) {
        this.error = 'Please select both start and end dates';
        return;
      }

      this.isLoading = true;
      this.error = null;
      this.hasSearched = true;

      try {
        const bodyData = {
          date_from: this.dateFrom,
          date_to: this.dateTo,
        };
        
        const url = `${this.subDataUrl}/bridge/rx_data/user_and_device/data_export/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.data && response.data.data) {
          this.dataList = response.data.data;
        } else {
          this.dataList = [];
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        this.error = 'Failed to fetch data. Please try again.';
        this.dataList = [];
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString();
    },

    getColumnClass(header) {
      const classMap = {
        'Account Name': 'account-name',
        'Active Name': 'active-name',
        'Email': 'email',
        'Phone Number': 'phone',
        'Plant Name': 'plant-name',
        'Create Time': 'create-time',
        'Datalog SN': 'datalog-sn',
        'Device SN': 'device-sn',
        'Device Type': 'device-type',
        'ID': 'id',
        'Plant Address': 'plant-address',
        'Plant ID': 'plant-id',
        'User ID': 'user-id',
        'Installer Company': 'installer-company',
        'Brand': 'brand'
      };
      return classMap[header] || '';
    },

    getBrand(item) {
      if (!item) return 'Renox';
      
      // Normalize strings for case-insensitive comparison
      const normalize = (str) => str ? String(str).trim().toLowerCase() : '';
      
      const parentCompany = normalize(item.parentCompany);
      const installerCompanyAccountName = normalize(item.installerCompany_AccountName);
      const grandparentCompany = normalize(item.grandparentCompany);
      const grandparentAccountName = normalize(item.grandparentAccountName);
      
      // Check if any of the fields match 'powerplus' (case-insensitive)
      if (parentCompany === 'powerplus' || 
          installerCompanyAccountName === 'powerplus' || 
          grandparentCompany === 'powerplus' ||
          grandparentAccountName === 'powerplus') {
        return 'PowerPlus';
      }
      
      return 'Renox';
    },

    getCellValue(item, column) {
      if (!item || !column) return '-';
      
      // Handle special formatters
      if (column.formatter === 'getBrand') {
        return this.getBrand(item);
      } else if (column.formatter === 'formatDate') {
        return this.formatDate(item[column.field]);
      }
      
      // Handle regular fields
      if (column.field) {
        return item[column.field] || '-';
      }
      
      return '-';
    },

    saveColumnVisibilityToCookies() {
      const visibilityState = this.columnConfig.map(col => ({
        header: col.header,
        visible: col.visible
      }));
      setCookie('data_extractor_column_visibility', JSON.stringify(visibilityState));
    },

    loadColumnVisibilityFromCookies() {
      const savedState = getCookie('data_extractor_column_visibility');
      if (savedState) {
        try {
          const visibilityState = JSON.parse(savedState);
          // Create a map for quick lookup
          const visibilityMap = {};
          visibilityState.forEach(item => {
            visibilityMap[item.header] = item.visible;
          });
          
          // Apply saved visibility state to columnConfig
          this.columnConfig.forEach(col => {
            if (visibilityMap.hasOwnProperty(col.header)) {
              col.visible = visibilityMap[col.header];
            }
          });
        } catch (error) {
          console.error('Error loading column visibility from cookies:', error);
        }
      }
    },

    toggleColumnVisibility(column) {
      column.visible = !column.visible;
      this.saveColumnVisibilityToCookies();
    },

    selectAllMarketingColumns() {
      this.columnConfig.forEach(col => col.visible = true);
      this.saveColumnVisibilityToCookies();
    },

    deselectAllMarketingColumns() {
      this.columnConfig.forEach(col => col.visible = false);
      this.saveColumnVisibilityToCookies();
    },

    exportToCSV() {
      if (this.dataList.length === 0) {
        this.error = 'No data to export';
        return;
      }

      const visibleColumns = this.visibleMarketingColumns;
      if (visibleColumns.length === 0) {
        this.error = 'Please select at least one column to export';
        return;
      }

      try {
        // Create CSV content with only visible columns
        const csvContent = this.convertToCSVWithColumns(this.dataList, visibleColumns);
        
        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `data_export_${this.dateFrom}_to_${this.dateTo}.csv`);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error exporting CSV:', error);
        this.error = 'Failed to export CSV file';
      }
    },

    convertToCSVWithColumns(data, columns) {
      if (data.length === 0 || columns.length === 0) return '';
      
      // Create CSV header row from visible columns
      const headers = columns.map(col => col.header);
      const csvRows = [headers.join(',')];
      
      // Add data rows
      for (const row of data) {
        const values = columns.map(column => {
          const value = this.getCellValue(row, column);
          // Handle values that contain commas, quotes, or newlines
          if (value === null || value === undefined || value === '-') return '';
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
        csvRows.push(values.join(','));
      }
      
      return csvRows.join('\n');
    },

    convertToCSV(data) {
      if (data.length === 0) return '';
      
      // Get headers from the first object
      const headers = Object.keys(data[0]);
      
      // Create CSV header row
      const csvRows = [headers.join(',')];
      
      // Add data rows
      for (const row of data) {
        const values = headers.map(header => {
          const value = row[header];
          // Handle values that contain commas, quotes, or newlines
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
        csvRows.push(values.join(','));
      }
      
      return csvRows.join('\n');
    },

    // Combined tab methods
    prepareLabels(intervalInMins, length) {
      const labels = [];
      let minutes = 0;
      for (let i = 0; i < length; i++) {
        const hours = Math.floor(minutes / 60).toString().padStart(2, '0');
        const mins = (minutes % 60).toString().padStart(2, '0');
        labels.push(`${hours}:${mins}`);
        minutes += intervalInMins;
      }
      return labels;
    },

    decodeModelData(encodedData) {
      const tryDecodingChunk = (chunk) => {
        try {
          const decoded = base64.decode(chunk);
          try {
            return { success: true, data: JSON.parse(decoded) };
          } catch {
            const numericString = decoded.toString().trim();
            if (numericString.match(/^[\d.,\s]+$/)) {
              const numbers = numericString
                .split(',')
                .map(n => parseFloat(n.trim()))
                .filter(n => !isNaN(n));
              if (numbers.length > 0) {
                return { success: true, data: numbers };
              }
            }
            return { success: false };
          }
        } catch {
          return { success: false };
        }
      };
      const chunks = encodedData.split(/[^A-Za-z0-9+/=]/);
      let bestResult = { success: false, data: null };
      let maxLength = 0;
      for (const chunk of chunks) {
        if (chunk.length < 4) continue;
        const result = tryDecodingChunk(chunk);
        if (result.success) {
          const dataLength = Array.isArray(result.data) ? result.data.length : 1;
          if (dataLength > maxLength) {
            maxLength = dataLength;
            bestResult = result;
          }
        }
      }
      return bestResult.success ? bestResult.data : null;
    },

    async fetchActualDataFromRedx(date, plantId) {
      try {
        const bodyData = {
          plantId: plantId,
          logDate: date.replace(/-/g, '_'),
        };
        const url = `${this.subDataUrl}/proxy/rx/plant/get_daily_data`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data) {
          const actualPower = response.data;
          if (Array.isArray(actualPower) && actualPower.length > 0) {
            const batPData = [];
            const gridPData = [];
            const loadPData = [];
            const ppvData = [];
            const socData = [];

            actualPower.forEach((dataPoint) => {
              if (dataPoint && typeof dataPoint === 'object') {
                const { batP, gridP, loadP, ppv, soc, logTime } = dataPoint;
                const index = this.chartLabels.indexOf(logTime);
                if (index !== -1) {
                  batPData[index] = batP != null ? parseFloat(batP).toFixed(2) : '';
                  gridPData[index] = gridP != null ? parseFloat(gridP).toFixed(2) : '';
                  loadPData[index] = loadP != null ? parseFloat(loadP).toFixed(2) : '';
                  ppvData[index] = ppv != null ? parseFloat(ppv).toFixed(2) : '';
                  socData[index] = soc != null ? parseFloat(soc).toFixed(2) : '';
                }
              }
            });

            return {
              batP: this.ensureArray(batPData),
              gridP: this.ensureArray(gridPData),
              loadP: this.ensureArray(loadPData),
              ppv: this.ensureArray(ppvData),
              soc: this.ensureArray(socData)
            };
          }
        }
        return {
          batP: [],
          gridP: [],
          loadP: [],
          ppv: [],
          soc: []
        };
      } catch (error) {
        console.error('Error fetching actual data:', error);
        return {
          batP: [],
          gridP: [],
          loadP: [],
          ppv: [],
          soc: []
        };
      }
    },

    async fetchPriceActualData(date, plantId) {
      try {
        const bodyData = {
          date: date,
          plant_id: plantId,
        };
        const url = `${this.subDataUrl}/price_actual/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data) {
          const actual_price_values = response.data;
          return {
            priceBuy: this.ensureArray(actual_price_values.price_buy_values),
            priceSell: this.ensureArray(actual_price_values.price_sell_values)
          };
        }
        return {
          priceBuy: [],
          priceSell: []
        };
      } catch (error) {
        console.error('Error fetching price actual data:', error);
        return {
          priceBuy: [],
          priceSell: []
        };
      }
    },

    async fetchPricePredictions(date, plantId) {
      try {
        const bodyData = {
          model_date: date,
          plant_id: plantId,
        };
        const url = `${this.subDataUrl}/price_prediction/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data) {
          const predictions = response.data;
          if (Array.isArray(predictions) && predictions.length > 0 && predictions[0]) {
            const modelPriceBuyData = predictions[0].model_price_buy;
            const modelPriceSellData = predictions[0].model_price_sell;
            const decodedModelPriceBuy = this.decodeModelData(modelPriceBuyData);
            const decodedModelPriceSell = this.decodeModelData(modelPriceSellData);
            
            return {
              priceBuy: this.ensureArray(decodedModelPriceBuy),
              priceSell: this.ensureArray(decodedModelPriceSell)
            };
          }
        }
        return {
          priceBuy: [],
          priceSell: []
        };
      } catch (error) {
        console.error('Error fetching price predictions:', error);
        return {
          priceBuy: [],
          priceSell: []
        };
      }
    },

    async fetchPvPredictions(date, plantId) {
      try {
        const bodyData = {
          model_date: date,
          plant_id: plantId,
        };
        const url = `${this.subDataUrl}/pv_prediction/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode == 0 && response.data != null) {
          const predictions = response.data;
          if (Array.isArray(predictions) && predictions.length > 0 && predictions[0]) {
            const modelPvData = predictions[0].model_pv;
            const decodedPv = this.decodeModelData(modelPvData);
            const modelAdjustedPvData = predictions[0].model_adjusted_pv;
            const decodedAdjustedPv = this.decodeModelData(modelAdjustedPvData);
            
            return {
              pv: this.ensureArray(decodedPv),
              pvAdjusted: this.ensureArray(decodedAdjustedPv)
            };
          }
        }
        return {
          pv: [],
          pvAdjusted: []
        };
      } catch (error) {
        console.error('Error fetching PV predictions:', error);
        return {
          pv: [],
          pvAdjusted: []
        };
      }
    },

    async fetchLoadPredictions(date, plantId) {
      try {
        const bodyData = {
          model_date: date,
          plant_id: plantId,
        };
        const url = `${this.subDataUrl}/load_prediction/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data) {
          const predictions = response.data;
          if (Array.isArray(predictions) && predictions.length > 0 && predictions[0]) {
            const modelPredictionLoadData = predictions[0].model_prediction_load;
            const decodedModelPredictionLoad = this.decodeModelData(modelPredictionLoadData);
            
            return {
              load: this.ensureArray(decodedModelPredictionLoad)
            };
          }
        }
        return {
          load: []
        };
      } catch (error) {
        console.error('Error fetching load predictions:', error);
        return {
          load: []
        };
      }
    },

    async fetchCombinedData() {
      if (!this.combinedDateFrom || !this.combinedDateTo || !this.combinedPlantId) {
        this.combinedError = 'Please select date range and plant ID';
        return;
      }

      this.isLoadingCombined = true;
      this.combinedError = null;
      this.combinedHasSearched = true;
      this.combinedPowerDatasets = [];
      this.combinedPriceDatasets = [];
      this.combinedTableData = [];
      this.combinedRawDays = [];
      this.selectedChartDates = [];
      this.combinedHiddenLegends = { power: [], price: [] };

      try {
        // Generate date range
        const dates = [];
        const startDate = new Date(this.combinedDateFrom);
        const endDate = new Date(this.combinedDateTo);
        
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          dates.push(d.toISOString().split('T')[0]);
        }

        // Fetch data for all dates
        const allDataPromises = dates.map(async (date) => {
          const [actualData, priceActual, pricePrediction, pvPrediction, loadPrediction] = await Promise.all([
            this.fetchActualDataFromRedx(date, this.combinedPlantId),
            this.fetchPriceActualData(date, this.combinedPlantId),
            this.fetchPricePredictions(date, this.combinedPlantId),
            this.fetchPvPredictions(date, this.combinedPlantId),
            this.fetchLoadPredictions(date, this.combinedPlantId)
          ]);

          return { date, actualData, priceActual, pricePrediction, pvPrediction, loadPrediction };
        });

        const allData = await Promise.all(allDataPromises);

        this.combinedRawDays = allData;
        this.chartDayMode = allData.length > 1 ? 'multiple' : 'single';
        this.selectedChartDates = allData.map(day => day.date);
        this.processCombinedData(allData);

      } catch (error) {
        console.error('Error fetching combined data:', error);
        this.combinedError = 'Failed to fetch data. Please try again.';
      } finally {
        this.isLoadingCombined = false;
      }
    },

    chartSeriesDefinitions() {
      return {
        power: [
          { key: 'actualBattery', group: 'battery_actual', name: 'Actual Battery Power', color: `rgba(200, 20, 150, ${this.opacityLevelActual})` },
          { key: 'actualGrid', group: 'grid_actual', name: 'Actual Grid Power', color: `rgba(54, 162, 235, ${this.opacityLevelActual})` },
          { key: 'actualLoad', group: 'load_actual', name: 'Actual Load Power', color: `rgba(153, 102, 255, ${this.opacityLevelActual})` },
          { key: 'actualPv', group: 'pv_actual', name: 'Actual PV Power', color: `rgba(75, 192, 50, ${this.opacityLevelActual})` },
          { key: 'predictedPv', group: 'pv_prediction', name: 'Predicted PV Power', color: `rgba(75, 192, 50, ${this.opacityLevelPrediction})` },
          { key: 'predictedLoad', group: 'load_prediction', name: 'Predicted Load', color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})` }
        ],
        price: [
          { key: 'actualPriceBuy', group: 'price_actual', name: 'Actual Price Buy', color: `rgba(153, 102, 255, ${this.opacityLevelActual})` },
          { key: 'actualPriceSell', group: 'price_actual', name: 'Actual Price Sell', color: `rgba(255, 159, 64, ${this.opacityLevelActual})` },
          { key: 'predictedPriceBuy', group: 'price_prediction', name: 'Predicted Price Buy', color: `rgba(153, 102, 255, ${this.opacityLevelPrediction})` },
          { key: 'predictedPriceSell', group: 'price_prediction', name: 'Predicted Price Sell', color: `rgba(255, 159, 64, ${this.opacityLevelPrediction})` }
        ]
      };
    },

    dayPointCount(dayData) {
      const actualData = dayData && dayData.actualData;
      const pvPrediction = dayData && dayData.pvPrediction;
      const loadPrediction = dayData && dayData.loadPrediction;
      return Math.max(
        actualData ? (this.ensureArray(actualData.batP).length || 0) : 0,
        pvPrediction ? (this.ensureArray(pvPrediction.pv).length || 0) : 0,
        loadPrediction ? (this.ensureArray(loadPrediction.load).length || 0) : 0,
        288
      );
    },

    pointTimeLabel(index) {
      return this.chartLabels[index] || `${Math.floor(index / 12)}:${String((index * 5) % 60).padStart(2, '0')}`;
    },

    setChartDayMode(mode) {
      if (mode === 'multiple' && this.availableChartDates.length < 2) return;
      if (this.chartDayMode === mode) return;
      this.chartDayMode = mode;
      if (mode === 'single') {
        const current = this.availableChartDates.find(date => this.selectedChartDates.includes(date));
        this.selectedChartDates = current ? [current] : (this.availableChartDates[0] ? [this.availableChartDates[0]] : []);
      }
      this.rebuildChartDatasets();
    },

    toggleChartDate(date) {
      if (this.chartDayMode === 'single') {
        this.selectedChartDates = [date];
      } else if (this.selectedChartDates.includes(date)) {
        this.selectedChartDates = this.selectedChartDates.filter(selected => selected !== date);
      } else {
        this.selectedChartDates = [...this.selectedChartDates, date];
      }
      this.rebuildChartDatasets();
    },

    selectAllChartDates() {
      this.selectedChartDates = [...this.availableChartDates];
      this.rebuildChartDatasets();
    },

    updateCombinedHiddenLegends(chartType, legends) {
      this.combinedHiddenLegends = {
        ...this.combinedHiddenLegends,
        [chartType]: Array.isArray(legends) ? [...new Set(legends)] : []
      };
    },

    rebuildChartDatasets() {
      const selected = new Set(this.selectedChartDates);
      const days = this.combinedRawDays
        .filter(day => selected.has(day.date))
        .slice()
        .sort((a, b) => a.date.localeCompare(b.date));

      if (days.length === 0) {
        this.combinedPowerDatasets = [];
        this.combinedPriceDatasets = [];
        return;
      }

      const multiDay = days.length > 1;
      const labels = [];
      const definitions = this.chartSeriesDefinitions();
      const valuesByKey = {};
      [...definitions.power, ...definitions.price].forEach(series => {
        valuesByKey[series.key] = [];
      });

      days.forEach(day => {
        const pointCount = this.dayPointCount(day);
        const dayRows = this.combinedTableData.filter(row => row.date === day.date);
        for (let i = 0; i < pointCount; i++) {
          const timeLabel = (dayRows[i] && dayRows[i].time) || this.pointTimeLabel(i);
          labels.push(multiDay ? `${day.date} ${timeLabel}` : timeLabel);
          const row = dayRows[i] || {};
          Object.keys(valuesByKey).forEach(key => {
            const value = row[key];
            valuesByKey[key].push(value !== null && value !== undefined ? value : '');
          });
        }
      });

      const toDatasets = (seriesList) => seriesList.reduce((datasets, series) => {
        const values = valuesByKey[series.key] || [];
        const hasValue = values.some(value => value !== '' && value !== null && value !== undefined);
        if (hasValue) {
          datasets.push({
            group: series.group,
            name: series.name,
            values,
            color: series.color,
            labels
          });
        }
        return datasets;
      }, []);

      this.combinedPowerDatasets = toDatasets(definitions.power);
      this.combinedPriceDatasets = toDatasets(definitions.price);
    },

    processCombinedData(allData) {
      const tableRows = [];

      allData.forEach((dayData) => {
        const { date, actualData, priceActual, pricePrediction, pvPrediction, loadPrediction } = dayData;
        const maxLength = this.dayPointCount(dayData);

        for (let i = 0; i < maxLength; i++) {
          tableRows.push({
            date: date,
            time: this.pointTimeLabel(i),
            actualBattery: this.safeArrayAccess(actualData, 'batP', i),
            actualGrid: this.safeArrayAccess(actualData, 'gridP', i),
            actualLoad: this.safeArrayAccess(actualData, 'loadP', i),
            actualPv: this.safeArrayAccess(actualData, 'ppv', i),
            actualSoc: this.safeArrayAccess(actualData, 'soc', i),
            predictedPv: this.safeArrayAccess(pvPrediction, 'pv', i),
            predictedPvAdjusted: this.safeArrayAccess(pvPrediction, 'pvAdjusted', i),
            predictedLoad: this.safeArrayAccess(loadPrediction, 'load', i),
            actualPriceBuy: this.safeArrayAccess(priceActual, 'priceBuy', i),
            actualPriceSell: this.safeArrayAccess(priceActual, 'priceSell', i),
            predictedPriceBuy: this.safeArrayAccess(pricePrediction, 'priceBuy', i),
            predictedPriceSell: this.safeArrayAccess(pricePrediction, 'priceSell', i)
          });
        }
      });

      this.combinedTableData = tableRows;
      this.rebuildChartDatasets();

      if (tableRows.length > 0) {
        this.availableColumns = [
          { key: 'date', label: 'Date' },
          { key: 'time', label: 'Time' },
          { key: 'actualBattery', label: 'Actual Battery Power (kW)' },
          { key: 'actualGrid', label: 'Actual Grid Power (kW)' },
          { key: 'actualLoad', label: 'Actual Load Power (kW)' },
          { key: 'actualPv', label: 'Actual PV Power (kW)' },
          { key: 'actualSoc', label: 'Actual SoC (%)' },
          { key: 'predictedPv', label: 'Predicted PV Power (kW)' },
          { key: 'predictedPvAdjusted', label: 'Predicted PV Power Adjusted (kW)' },
          { key: 'predictedLoad', label: 'Predicted Load (kW)' },
          { key: 'actualPriceBuy', label: 'Actual Price Buy (AUD)' },
          { key: 'actualPriceSell', label: 'Actual Price Sell (AUD)' },
          { key: 'predictedPriceBuy', label: 'Predicted Price Buy (AUD)' },
          { key: 'predictedPriceSell', label: 'Predicted Price Sell (AUD)' }
        ];
        this.selectedColumns = this.availableColumns.map(col => col.key);
      }
    },

    csvCell(value) {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    },

    extractCombinedData(chartType) {
      const definitions = this.chartSeriesDefinitions();
      const seriesList = definitions[chartType];
      if (!seriesList) return;

      const filename = chartType === 'power'
        ? `Power_Data_${this.combinedDateFrom}_to_${this.combinedDateTo}_Plant${this.combinedPlantId}.csv`
        : `Price_Data_${this.combinedDateFrom}_to_${this.combinedDateTo}_Plant${this.combinedPlantId}.csv`;
      const hiddenNames = chartType === 'power'
        ? this.combinedHiddenLegends.power
        : this.combinedHiddenLegends.price;

      const columns = seriesList.filter(series => {
        if (hiddenNames.includes(series.name)) return false;
        return this.combinedTableData.some(row => row[series.key] !== '' && row[series.key] !== null && row[series.key] !== undefined);
      });

      if (this.combinedTableData.length === 0 || columns.length === 0) {
        alert('No visible data to export');
        return;
      }

      const header = ['Date', 'Time', ...columns.map(series => series.name)].join(',');
      const rows = this.combinedTableData.map(row => {
        const values = [row.date, row.time, ...columns.map(series => row[series.key])];
        return values.map(value => this.csvCell(value)).join(',');
      });
      const csvContent = [header, ...rows].join('\n') + '\n';

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    exportCombinedDataToCSV() {
      if (this.selectedColumns.length === 0) {
        alert('Please select at least one column to export');
        return;
      }

      if (this.combinedTableData.length === 0) {
        alert('No data to export');
        return;
      }

      const visibleCols = this.visibleTableColumns;
      let csvContent = visibleCols.map(col => col.label).join(',');
      csvContent += '\n';

      this.combinedTableData.forEach(row => {
        const values = visibleCols.map(col => {
          // Use the same formatting logic as table display
          const value = this.formatTableValueWithOptions(row, col);
          if (value === null || value === undefined || value === '') return '';
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
        csvContent += values.join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Combined_Data_${this.combinedDateFrom}_to_${this.combinedDateTo}_Plant${this.combinedPlantId}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    selectAllColumns() {
      this.selectedColumns = this.availableColumns.map(col => col.key);
    },

    deselectAllColumns() {
      this.selectedColumns = [];
    },

    formatTableValue(value) {
      if (value === null || value === undefined || value === '') return '-';
      return value;
    },

    formatTableValueWithOptions(row, column) {
      // Handle combined datetime
      if (column.key === 'datetime') {
        const date = row.date || '';
        const time = row.time || '';
        if (!date || !time) return '-';
        
        // Format based on selected format
        return this.formatDateTime(date, time, this.tableOptions.dateTimeFormat);
      }

      // Handle plant ID
      if (column.key === 'plantId') {
        return this.combinedPlantId || '-';
      }

      // Handle regular columns
      return this.formatTableValue(row[column.key]);
    },

    formatDateTime(dateStr, timeStr, format) {
      if (!dateStr || !timeStr) return '-';
      
      try {
        // Parse date string (YYYY-MM-DD)
        const [year, month, day] = dateStr.split('-');
        const monthPadded = month.padStart(2, '0');
        const dayPadded = day.padStart(2, '0');
        
        // Parse time string (HH:mm)
        const [hours, minutes] = timeStr.split(':');
        const hoursPadded = hours.padStart(2, '0');
        const minutesPadded = minutes.padStart(2, '0');
        
        // Create date object for seconds if needed
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 
                             parseInt(hours), parseInt(minutes));
        const seconds = date.getSeconds().toString().padStart(2, '0');
        
        // Build formatted string based on format pattern
        let formatted = format;
        
        // Replace date parts - order matters for formats with slashes
        if (format.includes('DD/MM/YYYY')) {
          formatted = formatted.replace('DD/MM/YYYY', `${dayPadded}/${monthPadded}/${year}`);
        } else if (format.includes('MM/DD/YYYY')) {
          formatted = formatted.replace('MM/DD/YYYY', `${monthPadded}/${dayPadded}/${year}`);
        } else {
          // Handle YYYY-MM-DD format
          formatted = formatted.replace('YYYY', year);
          formatted = formatted.replace('MM', monthPadded);
          formatted = formatted.replace('DD', dayPadded);
        }
        
        // Replace time parts
        formatted = formatted.replace('HH', hoursPadded);
        formatted = formatted.replace('mm', minutesPadded);
        formatted = formatted.replace('ss', seconds);
        
        return formatted;
      } catch (error) {
        console.error('Error formatting datetime:', error);
        return `${dateStr} ${timeStr}`;
      }
    }
  },

  mounted() {
    // Set default dates (today and 4 days ago)
    const today = new Date();
    const fourDaysAgo = new Date();
    fourDaysAgo.setDate(today.getDate() - 4);
    
    this.dateTo = today.toISOString().split('T')[0];
    this.dateFrom = fourDaysAgo.toISOString().split('T')[0];
    
    // Initialize Combined tab default dates (today)
    this.combinedDateTo = today.toISOString().split('T')[0];
    this.combinedDateFrom = today.toISOString().split('T')[0];
    
    // Initialize subDataUrl from cookies
    this.readFromCookies();
    
    // Load column visibility from cookies
    this.loadColumnVisibilityFromCookies();
  },

  // Note: Environment changes are handled automatically by the apiService
  // based on the dev mode state from cookies, so no manual event listening is needed
};
</script>

<style scoped>
.page-container {
  padding: 20px;
  width: 100%;
  margin: 0 auto;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.header-section {
  text-align: center;
  margin-bottom: 30px;
}

.header-section h2 {
  color: var(--text-primary);
  margin-bottom: 8px;
  font-size: 2.2rem;
  font-weight: 600;
}

.subtitle {
  color: var(--text-secondary);
  font-size: 1rem;
  margin: 0;
}

/* Data Groups Tabs */
.data-groups-container {
  margin-bottom: 30px;
}

.tab-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

.tab-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 25px;
  border: 2px solid var(--border-color);
  border-radius: 12px;
  background: var(--card-bg);
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  position: relative;
  box-shadow: 0 2px 8px var(--shadow-color);
}

.tab-button.active {
  border-color: #3498db;
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);
}

.tab-button:not(.active):not(.disabled) {
  border-color: var(--border-color);
  background: var(--card-bg);
  color: var(--text-primary);
}

.tab-button.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--bg-tertiary);
  color: var(--text-tertiary);
}

.tab-button:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.tab-icon {
  font-size: 1.5rem;
  margin-bottom: 8px;
}

.tab-button span {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 5px;
}

.status-badge {
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.status-badge.active {
  background: rgba(0, 0, 0, 0.2);
  color: #27ae60;
}

.status-badge:not(.active):not(.disabled) {
  background: rgba(52, 152, 219, 0.2);
  color: #3498db;
}

.status-badge.disabled {
  background: rgba(231, 76, 60, 0.2);
  color: #e74c3c;
}

/* Date Range Container */
.date-range-container {
  background: var(--card-bg);
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  border: 1px solid var(--border-color);
}

.date-inputs {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.date-input-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.date-input-group label {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.date-input {
  padding: 10px 15px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.9rem;
  transition: border-color 0.3s ease;
  background: var(--input-bg);
  color: var(--text-primary);
}

.date-input:focus {
  outline: none;
  border-color: #3498db;
}

.fetch-button {
  background: linear-gradient(135deg, #27ae60, #2ecc71);
  color: white;
  border: none;
  padding: 12px 25px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.fetch-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(39, 174, 96, 0.3);
}

.fetch-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Loading State */
.loading-container {
  text-align: center;
  padding: 40px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--bg-tertiary);
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Data Section */
.data-section {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  overflow: hidden;
  width: 100%;
  border: 1px solid var(--border-color);
}

.column-visibility-container {
  background: var(--bg-secondary);
  padding: 20px 25px;
  border-bottom: 1px solid var(--border-color);
}

.column-visibility-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.column-visibility-header h4 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.1rem;
}

.column-visibility-actions {
  display: flex;
  gap: 10px;
}

.column-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  max-height: 200px;
  overflow-y: auto;
  padding: 10px;
  background: var(--card-bg);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.column-checkbox-item {
  display: flex;
  align-items: center;
  padding: 6px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.column-checkbox-item:hover {
  background-color: var(--bg-tertiary);
}

.column-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 10px;
  cursor: pointer;
  accent-color: #3498db;
}

.column-label {
  font-size: 0.9rem;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.select-all-button,
.deselect-all-button {
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;
}

.select-all-button {
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
}

.select-all-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
}

.deselect-all-button {
  background: #e74c3c;
  color: white;
}

.deselect-all-button:hover {
  background: #c0392b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
}

.export-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.table-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.2rem;
}

.export-button {
  background: linear-gradient(135deg, #e67e22, #f39c12);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.export-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(230, 126, 34, 0.3);
}

/* Table Styles */
.table-container {
  overflow-x: auto;
  max-height: 600px;
  overflow-y: auto;
  width: 100%;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  table-layout: fixed;
  min-width: 1400px; /* Ensure minimum width for all columns */
}

.data-table th {
  background: #34495e;
  color: white;
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.data-table td {
  padding: 10px 8px;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
}

.data-table tbody tr:nth-child(even) {
  background: var(--bg-secondary);
}

.data-table tbody tr:hover {
  background: var(--bg-tertiary);
}

/* Column-specific widths */
.data-table th.account-name,
.data-table td.account-name {
  width: 120px;
  min-width: 120px;
}

.data-table th.active-name,
.data-table td.active-name {
  width: 120px;
  min-width: 120px;
}

.data-table th.email,
.data-table td.email {
  width: 180px;
  min-width: 180px;
}

.data-table th.phone,
.data-table td.phone {
  width: 120px;
  min-width: 120px;
}

.data-table th.plant-name,
.data-table td.plant-name {
  width: 200px;
  min-width: 200px;
}

.data-table th.create-time,
.data-table td.create-time {
  width: 140px;
  min-width: 140px;
}

.data-table th.datalog-sn,
.data-table td.datalog-sn {
  width: 120px;
  min-width: 120px;
}

.data-table th.device-sn,
.data-table td.device-sn {
  width: 150px;
  min-width: 150px;
}

.data-table th.device-type,
.data-table td.device-type {
  width: 100px;
  min-width: 100px;
}

.data-table th.id,
.data-table td.id {
  width: 80px;
  min-width: 80px;
}

.data-table th.plant-address,
.data-table td.plant-address {
  width: 250px;
  min-width: 250px;
}

.data-table th.plant-id,
.data-table td.plant-id {
  width: 80px;
  min-width: 80px;
}

.data-table th.user-id,
.data-table td.user-id {
  width: 80px;
  min-width: 80px;
}

.data-table th.installer-company,
.data-table td.installer-company {
  width: 150px;
  min-width: 150px;
}

.data-table th.brand,
.data-table td.brand {
  width: 120px;
  min-width: 120px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  border: 1px solid var(--border-color);
}

.empty-icon {
  font-size: 3rem;
  color: var(--text-tertiary);
  margin-bottom: 20px;
}

.empty-state h3 {
  color: var(--text-primary);
  margin-bottom: 10px;
}

.empty-state p {
  color: var(--text-secondary);
  margin: 0;
}

/* Error State */
.error-container {
  text-align: center;
  padding: 40px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  margin-top: 20px;
}

.error-icon {
  font-size: 2.5rem;
  color: #e53e3e;
  margin-bottom: 15px;
}

.error-container h3 {
  color: var(--text-primary);
  margin-bottom: 10px;
}

.error-container p {
  color: var(--text-secondary);
  margin-bottom: 20px;
}

.retry-button {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.retry-button:hover {
  background: #c53030;
  transform: translateY(-1px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-container {
    padding: 15px;
  }
  
  .header-section h2 {
    font-size: 1.8rem;
  }
  
  .tab-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .tab-button {
    width: 100%;
    max-width: 200px;
  }
  
  .date-range-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .date-inputs {
    flex-direction: column;
  }
  
  .table-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .data-table {
    font-size: 0.75rem;
    min-width: 1200px;
  }
  
  .data-table th,
  .data-table td {
    padding: 6px 4px;
  }
}

@media (max-width: 480px) {
  .data-table {
    font-size: 0.7rem;
    min-width: 1000px;
  }
  
  .data-table th,
  .data-table td {
    padding: 4px 2px;
  }
}

/* Combined Tab Styles */
.combined-controls-container {
  margin-bottom: 30px;
}

.view-toggle-container {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
  margin-bottom: 20px;
}

.view-toggle-button {
  padding: 12px 25px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--text-primary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.view-toggle-button:hover {
  border-color: #3498db;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.2);
}

.view-toggle-button.active {
  border-color: #3498db;
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);
}

.combined-chart-section {
  margin-bottom: 30px;
}

.chart-day-picker {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  padding: 20px;
  margin-bottom: 20px;
}

.chart-day-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.chart-day-picker-header h5 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.1rem;
}

.chart-day-mode {
  display: flex;
  gap: 10px;
}

.chart-day-mode-button {
  padding: 8px 16px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--text-primary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chart-day-mode-button:hover:not(:disabled) {
  border-color: #3498db;
}

.chart-day-mode-button.active {
  border-color: #3498db;
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
}

.chart-day-mode-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chart-day-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chart-day-chip {
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 999px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}

.chart-day-chip.active {
  border-color: #3498db;
  background: rgba(52, 152, 219, 0.15);
  color: #3498db;
}

.chart-day-actions {
  margin-top: 14px;
}

.chart-day-hint {
  margin: 12px 0 0;
  color: var(--text-secondary);
  font-size: 0.85rem;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid var(--border-color);
}

.chart-header h5 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.2rem;
}

.chart-controls {
  display: flex;
  gap: 10px;
}

.custom-button {
  background: linear-gradient(135deg, #27ae60, #2ecc71);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.custom-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(39, 174, 96, 0.3);
}

.display-text-container {
  padding: 20px;
  text-align: center;
  color: var(--text-tertiary);
  font-style: italic;
}

.combined-table-section {
  margin-bottom: 30px;
}

.table-controls-container {
  background: var(--card-bg);
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  border: 1px solid var(--border-color);
}

.column-selector-container {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 20px;
  background: var(--bg-secondary);
}

.column-selector-container h4 {
  margin: 0 0 15px 0;
  color: var(--text-primary);
  font-size: 1.1rem;
}

.column-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
  padding: 10px;
  background: var(--card-bg);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.column-checkbox-item {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.column-checkbox-item:hover {
  background-color: var(--bg-tertiary);
}

.column-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 10px;
  cursor: pointer;
  accent-color: #3498db;
}

.column-label {
  font-size: 0.9rem;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.column-selector-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

.select-all-button,
.deselect-all-button {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;
}

.select-all-button {
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
}

.select-all-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
}

.deselect-all-button {
  background: #e74c3c;
  color: white;
}

.deselect-all-button:hover {
  background: #c0392b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
}

.export-container {
  display: flex;
  justify-content: flex-end;
}

.chart-container {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  margin-bottom: 20px;
  padding: 0 0 20px 0;
  border: 1px solid var(--border-color);
}

/* Table Options Styles */
.table-options-container {
  background: var(--card-bg);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  margin-bottom: 20px;
  border: 1px solid var(--border-color);
}

.table-options-container h4 {
  margin: 0 0 15px 0;
  color: var(--text-primary);
  font-size: 1.1rem;
}

.table-options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  align-items: center;
}

.table-option-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-option-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #3498db;
  background-color: var(--input-bg);
  border: 2px solid var(--border-color);
}

.table-option-label {
  font-size: 0.9rem;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.table-option-select {
  padding: 8px 12px;
  border: 2px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.9rem;
  color: var(--text-primary);
  background: var(--input-bg);
  cursor: pointer;
  transition: border-color 0.3s ease;
  min-width: 200px;
}

.table-option-select:focus {
  outline: none;
  border-color: #3498db;
}

.table-option-item label.table-option-label:not([for]) {
  cursor: default;
  margin-right: 5px;
}

@media (max-width: 768px) {
  .chart-day-picker-header,
  .chart-day-mode {
    flex-direction: column;
    align-items: stretch;
  }

  .chart-day-mode-button {
    width: 100%;
  }

  .view-toggle-container {
    flex-direction: column;
    align-items: stretch;
  }

  .view-toggle-button {
    width: 100%;
    justify-content: center;
  }

  .column-checkboxes {
    grid-template-columns: 1fr;
  }

  .table-controls-container {
    padding: 15px;
  }

  .chart-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .table-options-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .table-option-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .table-option-select {
    width: 100%;
    min-width: unset;
  }

  .table-options-container {
    padding: 15px;
  }
}
</style> 